import {
  Home as HomeIcon,
  Hammer,
  Bath,
  Paintbrush,
  Layers,
  Zap,
  Siren,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    num: "01",
    title: "Legal Basements",
    description:
      "Code-compliant basement apartments with permits, separate entrances, fire safety & rental-ready finishing.",
    icon: HomeIcon,
    image:
      "https://static.prod-images.emergentagent.com/jobs/9c39dd25-bc0f-419c-a6ee-60a4c93dfd8b/images/da3f92eb2e712dafa76dc37c816a8a3607903f13f0f8e351b716ca51717bd5b5.png",
  },
  {
    num: "02",
    title: "Custom Homes",
    description:
      "Ground-up construction tailored to your vision — design, permits, and execution under one roof.",
    icon: Hammer,
    image:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?fm=jpg&q=85&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  },
  {
    num: "03",
    title: "Kitchen & Bath",
    description:
      "Full kitchen and bathroom transformations — cabinetry, countertops, tiling, plumbing & lighting.",
    icon: Bath,
    image:
      "https://images.unsplash.com/photo-1682888813913-e13f18692019?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBraXRjaGVuJTIwcmVtb2RlbHxlbnwwfHx8fDE3NzkxMzQ4MzZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "04",
    title: "Painting",
    description:
      "Interior & exterior painting with premium-grade paints, clean prep work and detail-perfect finish.",
    icon: Paintbrush,
    image:
      "https://static.prod-images.emergentagent.com/jobs/9c39dd25-bc0f-419c-a6ee-60a4c93dfd8b/images/5693d252febbebc441119d363e18fbf86b24d90e579c8035baa8c64cd384d2dd.png",
  },
  {
    num: "05",
    title: "Flooring",
    description:
      "Hardwood, engineered wood, laminate, vinyl and tile — supply, installation and refinishing.",
    icon: Layers,
    image:
      "https://static.prod-images.emergentagent.com/jobs/9c39dd25-bc0f-419c-a6ee-60a4c93dfd8b/images/ebbb87324191b0c2940c8e85e41e49198cb0f03d2314f6b9b0de069be3b43868.png",
  },
  {
    num: "06",
    title: "Electric & Plumbing",
    description:
      "Licensed electricians and plumbers for upgrades, rough-ins, fixtures, panels and full inspections.",
    icon: Zap,
    image:
      "https://static.prod-images.emergentagent.com/jobs/9c39dd25-bc0f-419c-a6ee-60a4c93dfd8b/images/4497cc9f81bb573eec3477151260ca7ebdea9b255a2353ffc6975682779dda67.png",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="py-24 lg:py-36 bg-[#FAF7F2]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 lg:mb-24">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#B08D5B]" />
              <span className="label-overline text-[10px] text-[#B08D5B]">
                Our Discipline
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-[#0B1B33] leading-[1.05]">
              End-to-end remodeling,
              <br />
              <span className="italic text-[#B08D5B]">built right</span> the first time.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <p className="text-stone-600 font-body text-base lg:text-[15px] max-w-md leading-[1.85]">
              From a single washroom upgrade to a fully legal basement suite — our licensed
              tradespeople handle every part of the build, in-house. No subcontracting roulette.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#EDE6D8]" data-testid="services-grid">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                className="group bg-[#FAF7F2] hover:bg-white p-8 lg:p-10 flex flex-col transition-all duration-700 relative overflow-hidden"
                data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-heading text-sm text-[#B08D5B] font-medium tracking-wider">
                    {s.num}
                  </span>
                  <Icon className="w-5 h-5 text-stone-400 group-hover:text-[#B08D5B] transition-colors duration-500" />
                </div>

                <div className="relative h-44 mb-7 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[40%] group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-[#0B1B33]/10 group-hover:bg-transparent transition-colors duration-500" />
                </div>

                <h3 className="font-heading text-2xl text-[#0B1B33] mb-3 font-medium">
                  {s.title}
                </h3>
                <p className="text-stone-600 text-[14px] leading-[1.75] font-body flex-1">
                  {s.description}
                </p>
                <a
                  href="#contact"
                  className="mt-7 inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.2em] uppercase text-[#0B1B33] group-hover:text-[#B08D5B] transition-all"
                  data-testid={`service-link-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  Request Estimate
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </article>
            );
          })}

          {/* Emergency Services Card - dark navy with gold */}
          <article
            className="group relative bg-[#0B1B33] text-white p-8 lg:p-10 flex flex-col transition-all duration-500 overflow-hidden"
            data-testid="service-card-emergency-services"
          >
            <div className="absolute inset-0 grain pointer-events-none" />
            <div className="relative flex items-start justify-between mb-8">
              <span className="font-heading text-sm text-[#D4B888] font-medium tracking-wider">
                07
              </span>
              <span className="label-overline text-[9px] text-[#fca5a5] bg-[#DC2626]/15 border border-[#DC2626]/40 px-2.5 py-1">
                24 / 7
              </span>
            </div>

            <div className="relative w-16 h-16 mb-6 border border-[#DC2626]/60 flex items-center justify-center">
              <Siren className="w-7 h-7 text-[#DC2626]" />
            </div>

            <h3 className="relative font-heading text-2xl mb-3 font-medium">
              Emergency Services
            </h3>
            <p className="relative text-stone-300 text-[14px] leading-[1.75] font-body flex-1">
              Burst pipes, electrical faults, flooded basements? Our crew responds across the
              GTA, day or night — typically within 60 minutes.
            </p>
            <a
              href="tel:+16474466869"
              className="relative mt-7 inline-flex items-center gap-2.5 px-5 py-3 bg-[#DC2626] hover:bg-[#b91c1c] text-white font-semibold text-[11px] tracking-[0.2em] uppercase transition-all duration-300 w-fit"
              data-testid="emergency-card-call-btn"
            >
              <Siren className="w-3.5 h-3.5" />
              Call Emergency Line
            </a>
          </article>
        </div>
      </div>
    </section>
  );
}
